import { useEffect, useMemo, useState } from "react";
import { Admin } from "./Admin";
import { api, ApiFailure, ERROR_COPY, type Overview } from "./api";
import { Centered, Header, Shell } from "./components/Shell";
import { GAME_META, HUB_GROUPS, type GameId } from "./route";
import { getInitData } from "./telegram";

const ARCHIVE_DAYS = 7;

/** Last known switched-off ids, so the first paint doesn't flash games that then vanish. */
const OFF_CACHE_KEY = "games.off";

function cachedOff(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(OFF_CACHE_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function rememberOff(off: string[]): void {
  try {
    localStorage.setItem(OFF_CACHE_KEY, JSON.stringify(off));
  } catch {
    // storage blocked — the flash is cosmetic
  }
}

function addDaysIso(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function Hub({ onOpen }: { onOpen: (g: GameId, date?: string) => void }) {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [archiveDate, setArchiveDate] = useState<string | null>(null);
  // Statuses for the selected archive day, keyed by date to survive quick taps.
  const [archiveData, setArchiveData] = useState<Overview | null>(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [initialOff] = useState(cachedOff);

  const load = async () => {
    setError(null);
    if (!getInitData()) {
      setError(ERROR_COPY.unauthorized ?? "");
      return;
    }
    try {
      const o = await api.overview();
      setData(o);
      rememberOff(o.off);
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      setError(ERROR_COPY[code] ?? ERROR_COPY.server_error ?? "");
    }
  };
  useEffect(() => {
    void load();
  }, []);

  // Fetch the archive day's statuses when a chip is picked.
  useEffect(() => {
    if (!archiveDate) {
      setArchiveData(null);
      return;
    }
    let stale = false;
    setArchiveData(null);
    api
      .overview(archiveDate)
      .then((o) => {
        if (!stale) setArchiveData(o);
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [archiveDate]);

  // Last N Kyiv days, newest first — anchored on the server's date.
  const archiveDays = useMemo(() => {
    if (!data) return [];
    return Array.from({ length: ARCHIVE_DAYS }, (_, i) => addDaysIso(data.date, -(i + 1)));
  }, [data]);

  if (adminOpen) {
    return (
      <Admin
        onClose={(offToday) => {
          setAdminOpen(false);
          if (offToday) setData((d) => (d ? { ...d, off: offToday } : d));
          void load();
        }}
      />
    );
  }

  if (error) {
    return (
      <Shell>
        <Centered>
          <div>{error}</div>
          <button type="button" onClick={() => void load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
            ще раз
          </button>
        </Centered>
      </Shell>
    );
  }

  const shown = archiveDate ? archiveData : data;
  // Switched-off games leave the list; a section left empty goes with them.
  const off = new Set(shown?.off ?? data?.off ?? initialOff);
  const groups = HUB_GROUPS.map((group) => ({ title: group.title, games: group.games.filter((id) => !off.has(id)) })).filter(
    (group) => group.games.length > 0,
  );

  return (
    <Shell
      header={
        <Header
          left={archiveDate ?? data?.date ?? ""}
          title={archiveDate ? "АРХІВ" : "ІГРИ"}
          right={
            <div className="flex items-center gap-1.5">
              {data?.admin && !archiveDate && (
                <button
                  type="button"
                  aria-label="вимкнення ігор"
                  onClick={() => setAdminOpen(true)}
                  className="rounded-lg bg-[rgba(128,128,128,0.15)] px-2 py-1 text-sm active:opacity-70"
                >
                  ⚙️
                </button>
              )}
              <button
                type="button"
                onClick={() => setArchiveDate((d) => (d ? null : (archiveDays[0] ?? null)))}
                className="rounded-lg bg-[rgba(128,128,128,0.15)] px-2 py-1 text-sm active:opacity-70"
              >
                {archiveDate ? "✕ назад" : "📼 архів"}
              </button>
            </div>
          }
        />
      }
    >
      {archiveDate && (
        <div className="px-4 pb-2">
          <div className="text-xs text-tghint">пропущені дні — без стріка і посту в чат</div>
          <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
            {archiveDays.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setArchiveDate(d)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${
                  d === archiveDate ? "bg-tgbutton text-tgbuttontext" : "bg-[rgba(128,128,128,0.15)]"
                }`}
              >
                {d.slice(5)}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+12px)]">
        {groups.map((group) => (
          <section key={group.title}>
            <div className="sticky top-0 z-10 bg-tgbg pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-tghint">
              {group.title}
            </div>
            <div className="space-y-2">
              {group.games.map((id) => {
                const meta = GAME_META[id];
                const g = shown?.games[id];
                const status =
                  !g || g.status === null || g.status === "playing"
                    ? g?.status === "playing"
                      ? "▶ продовжити"
                      : archiveDate
                        ? "📼 грати"
                        : "грати"
                    : g.status === "won"
                      ? `✅ ${g.score ?? ""}`.trim()
                      : "🏳";
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onOpen(id, archiveDate ?? undefined)}
                    className="flex w-full items-center gap-3 rounded-2xl bg-tgsection px-4 py-3 text-left active:opacity-80"
                  >
                    <div className="text-2xl">{meta.emoji}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold tracking-wide">{meta.title}</span>
                        {g && <span className="text-xs text-tghint">#{g.number}</span>}
                      </div>
                      <div className="truncate text-xs text-tghint">{meta.blurb}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${g?.status === "won" ? "text-ok" : ""}`}>
                        {shown ? status : "…"}
                      </div>
                      {!archiveDate && g && g.played > 0 && <div className="text-[11px] text-tghint">грали: {g.played}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </Shell>
  );
}
